import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendMessageToParent } from '../utils/helper';
import { getLocalStorage } from '../utils/localStorage';
import { MOBILE_NUMBER } from '../pages/symptomCollector/utils/constants';

const SWIPE_CONFIG = {
  minDistance: 100,
  maxDuration: 500,
  maxVerticalMovement: 50,
  horizontalRatio: 1.5,
  edgeThreshold: 0.7,
  routesThatGoHome: ['/tatva-shots-home', '/tatva-shots'],
  customHandlerRoutes: ['/tatva-ai'],
  skipSelectors: [
    '.slick-slider',
    '.ant-carousel',
    '.carousel-card',
    'input',
    'textarea',
    '[contenteditable]',
  ],
} as const;
export const useSwipeNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);

  const handleBackNavigation = useCallback(() => {
    if (isNavigatingRef.current) return;

    // If on the home page, attempt to close the app (PWA/WebView/Native behavior)
    // This prevents navigating to previous history entries like /tatva-shots-home
    if (location.pathname === '/' || location.pathname === '') {
      // Try multiple methods to close the app for maximum compatibility
      
      // Method 1: Send message to React Native WebView (highest priority)
      try {
        sendMessageToParent('closeApp', {});
      } catch (e) {
        // Continue to other methods if this fails
      }
      
      // Method 2: Try Android WebView interface
      if ((window as any).Android && typeof (window as any).Android.closeApp === 'function') {
        try {
          (window as any).Android.closeApp();
          return; // Exit if successful
        } catch (e) {
          // Continue to other methods
        }
      }
      
      // Method 3: Try iOS WebKit message handler
      if ((window as any).webkit?.messageHandlers?.closeApp) {
        try {
          (window as any).webkit.messageHandlers.closeApp.postMessage({});
          return; // Exit if successful
        } catch (e) {
          // Continue to other methods
        }
      }
      
      // Method 4: Standard window.close() - works for windows opened by JS
      try {
        window.close();
      } catch (e) {
        // Continue to other methods
      }
      
      // Method 5: If in an iframe/WebView, send postMessage to parent
      if (window.parent !== window) {
        try {
          window.parent.postMessage({ type: 'CLOSE_APP', action: 'close' }, '*');
        } catch (e) {
          // Ignore cross-origin errors
        }
      }
      
      // Method 6: For standalone PWA, navigate to a system page to minimize
      if (window.matchMedia('(display-mode: standalone)').matches) {
        try {
          // If no history, user opened the app directly
          if (window.history.length <= 1) {
            // Attempt to navigate to system back which might minimize the app
            (navigator as any).app?.exitApp?.();
          }
        } catch (e) {
          // Final fallback - do nothing (app stays open)
        }
      }
      
      // Exit early - don't navigate if we're on home page
      return;
    }
    
    isNavigatingRef.current = true;
    
    const shouldGoHome = SWIPE_CONFIG.routesThatGoHome.some(route => route === location.pathname);
    if (shouldGoHome) {
      navigate('/', { replace: true });
    } else {
      navigate(-1);
    }
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 100);
  }, [navigate, location.pathname]);
  useEffect(() => {
    // If user is logged in as AA, then don't show the swipe navigation
    const isAALoggedIn = getLocalStorage(MOBILE_NUMBER);
    if (isAALoggedIn) {
      return;
    }
    const hasCustomHandler = SWIPE_CONFIG.customHandlerRoutes.some(route => route === location.pathname);
    if (hasCustomHandler) {
      return;
    }
    let startX = 0;
    let startY = 0;
    let touchStartTime = 0;
    let maxDiffY = 0;
    let isHorizontalScrolling = false;
    const shouldSkipElement = (target: HTMLElement): boolean => {
      for (const selector of SWIPE_CONFIG.skipSelectors) {
        if (target.closest(selector)) {
          return true;
        }
      }
      if (target.closest('.ant-tabs') || target.closest('.custom-tabs')) {
        return true;
      }
      const scrollableParent = target.closest('.overflow-x-scroll') || 
                               target.closest('[class*="overflow-x-scroll"]') ||
                               target.closest('.overflow-x-auto') ||
                               target.closest('[class*="overflow-x-auto"]');
      
      if (scrollableParent) {
        const element = scrollableParent as HTMLElement;
        if (element.scrollWidth > element.clientWidth) {
          return true;
        }
      }
      return false;
    };

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (shouldSkipElement(target)) {
        return;
      }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      touchStartTime = Date.now();
      maxDiffY = 0;
      isHorizontalScrolling = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!startX || !startY) return;

      const currentY = e.touches[0].clientY;
      const diffY = Math.abs(startY - currentY);
      maxDiffY = Math.max(maxDiffY, diffY);
      const currentX = e.touches[0].clientX;
      const diffX = Math.abs(startX - currentX);
      if (diffX > 10 && diffX > diffY * 1.5) {
        const target = e.target as HTMLElement;
        if (shouldSkipElement(target)) {
          isHorizontalScrolling = true;
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (isHorizontalScrolling) {
        startX = 0;
        startY = 0;
        maxDiffY = 0;
        isHorizontalScrolling = false;
        return;
      }
      if (!startX || !startY) {
        return;
      }

      const endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      const touchDuration = Date.now() - touchStartTime;

      const isLeftSwipe = diffX > SWIPE_CONFIG.minDistance;
      const isFastSwipe = touchDuration < SWIPE_CONFIG.maxDuration;
      const isHorizontal = maxDiffY < SWIPE_CONFIG.maxVerticalMovement || 
                          Math.abs(diffX) > maxDiffY * SWIPE_CONFIG.horizontalRatio;
      const startFromRightEdge = startX > window.innerWidth * SWIPE_CONFIG.edgeThreshold;

      if (isLeftSwipe && isFastSwipe && (isHorizontal || startFromRightEdge)) {
        e.preventDefault?.();
        handleBackNavigation();
      }

      startX = 0;
      startY = 0;
      maxDiffY = 0;
      isHorizontalScrolling = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true, capture: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: false, capture: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart, { capture: true } as any);
      document.removeEventListener('touchmove', handleTouchMove, { capture: true } as any);
      document.removeEventListener('touchend', handleTouchEnd, { capture: true } as any);
    };
  }, [handleBackNavigation, location.pathname]);
};

export default useSwipeNavigation;
